import { format, isValid, isSameDay, parse, parseISO } from "date-fns";
import { ERROR_MESSAGES } from "./lang";

export const isOverCharacterLimit = (input: string, limit: number) => {
  return input.length > limit;
};

const ALLOWED_ACCENTED_CHARACTERS =
  "ãñõáéíóúàèìòùâêîôûäëïöüÿåçøÃÑÕÁÉÍÓÚÀÈÌÒÙÂÊÎÔÛÄËÏÖÜŸÅÇØ";

//identify if the input contains non-ascii characters except for most common accents
export const containsNonAsciiCharacters = (input: string) => {
  return new RegExp(`[^\\x00-\\x7F${ALLOWED_ACCENTED_CHARACTERS}]`).test(input);
};

export const containsMoreThanFiveAccentedCharacters = (input: string) => {
  const accentedCharRegExp = new RegExp(
    `[${ALLOWED_ACCENTED_CHARACTERS}]`,
    "g",
  );
  return (input.match(accentedCharRegExp) || []).length > 5;
};

//determines if the input contains characters that may throw an error for twilio's length limit, which appear to be triggered by a high density of special characters
export const shouldWarnForUnsafeCharacters = (input: string) => {
  return (
    containsNonAsciiCharacters(input) ||
    containsMoreThanFiveAccentedCharacters(input)
  );
};

/**
 * Formats the appointment time range into a string representing the duration of
 * the appointment.
 *
 * @param appointmentStartTime - ISO string of appointment start time
 * @param appointmentEndTime - ISO string of appointment end time
 * @returns Formatted appointment time range as a string
 * @example
 * formatAppointmentTime("2026-01-14T18:00:00Z", "2026-01-14T18:30:00Z") // "Jan 14, 10:00 AM – 10:30 AM"
 */
export const formatAppointmentTime = (
  appointmentStartTime?: string | null,
  appointmentEndTime?: string | null,
): string => {
  if (!appointmentStartTime || !appointmentEndTime) {
    return "";
  }

  const start = parseISO(appointmentStartTime);
  const end = parseISO(appointmentEndTime);

  const startOnSameDay = isSameDay(start, end);

  if (startOnSameDay) {
    return `${format(start, "MMM d")}, ${format(start, "h:mm a")} – ${format(end, "h:mm a")}`;
  }

  return `${format(start, "MMM d")}, ${format(start, "h:mm a")} – ${format(end, "MMM d")}, ${format(end, "h:mm a")}`;
};

// Based on current data in production, plus 50% additional buffer
const MAX_NAME_INPUT_LENGTH = 60;

/** Validates a name string. */
function isValidName(name: string): [boolean, string | undefined] {
  if (!name.trim()) return [false, ERROR_MESSAGES.required];
  if (name.trim().length > MAX_NAME_INPUT_LENGTH) {
    return [false, ERROR_MESSAGES.inputTooLong];
  }
  return [true, undefined];
}

// Based on current data in production, plus 100% additional buffer
const MAX_EMAIL_INPUT_LENGTH = 150;

// Copied from dashboard/grow-therapy-frontend/libs/utils/src/lib/utils.ts
export const EMAIL_REGEX =
  // eslint-disable-next-line no-control-regex
  /^\s*(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])\s*$/;

/** Validates an email address using the standard regex pattern. */
function isValidEmail(email: string): [boolean, string | undefined] {
  if (!email.trim()) return [false, ERROR_MESSAGES.required];
  if (email.trim().length > MAX_EMAIL_INPUT_LENGTH) {
    return [false, ERROR_MESSAGES.inputTooLong];
  }
  return EMAIL_REGEX.test(email.trim())
    ? [true, undefined]
    : [false, ERROR_MESSAGES.invalidEmail];
}

/** Validates a date string in MM/DD/YYYY format. */
function isValidDateOfBirth(dateString: string): [boolean, string | undefined] {
  if (!dateString.trim()) return [false, ERROR_MESSAGES.required];
  return isValid(parse(dateString, "MM/dd/yyyy", new Date()))
    ? [true, undefined]
    : [false, ERROR_MESSAGES.invalidDateOfBirth];
}

/** Validates a phone number string containing exactly 4 digits. */
function isValidLastFourDigitsOfPhoneNumber(
  phoneNumber: string,
): [boolean, string | undefined] {
  if (!phoneNumber.trim()) return [false, ERROR_MESSAGES.required];
  return phoneNumber.trim().length === 4
    ? [true, undefined]
    : [false, ERROR_MESSAGES.invalidLastFourDigitsOfPhoneNumber];
}

export const PII_VALIDATORS = {
  firstName: isValidName,
  lastName: isValidName,
  email: isValidEmail,
  dateOfBirth: isValidDateOfBirth,
  lastFourDigitsOfPhoneNumber: isValidLastFourDigitsOfPhoneNumber,
};

/** Converts a date string in MM/DD/YYYY format to ISO date string (YYYY-MM-DD). */
export function convertToISODate(dateString: string): string | null {
  const [isValid] = PII_VALIDATORS.dateOfBirth(dateString);
  if (!isValid) return null;

  const parsedDate = parse(dateString, "MM/dd/yyyy", new Date());
  return format(parsedDate, "yyyy-MM-dd");
}
